/**
 * NMS PDP Lowest Price Component
 * Displays the lowest price from available discounts
 * @extends NMSComponent
 */

// Define base NMS Component class if not already defined
window.NMSComponent =
  window.NMSComponent ||
  class {
    constructor(element) {
      this.element = element;
    }

    init() {}

    dispatchCustomEvent(eventName, detail = {}) {
      const event = new CustomEvent(`nms:${eventName}`, {
        detail: {
          component: this,
          element: this.element,
          ...detail,
          timestamp: Date.now(),
        },
        bubbles: true,
        cancelable: true,
      });

      this.element.dispatchEvent(event);
    }

    destroy() {}
  };

// Ensure NMS namespace exists
window.neemans = window.neemans || {};

/**
 * NMS PDP Lowest Price Component
 */
class NMSPDPLowestPrice extends NMSComponent {
  static componentName = 'nms-pdp-lowest-price';

  constructor(element) {
    super(element);

    // Cache DOM elements
    this.lowestPriceAmount = this.element.querySelector('[data-nms-js="lowest-price-amount"]');
    this.scrollButton = this.element.querySelector('[data-nms-js="scroll-to-offers"]');

    // Bind methods
    this.handleDiscountCalculated = this.handleDiscountCalculated.bind(this);
    this.handleScrollButtonClick = this.handleScrollButtonClick.bind(this);
    this.handleScrollButtonKeydown = this.handleScrollButtonKeydown.bind(this);

    this.init();
  }

  init() {
    this.bindEvents();
    this.updatePrice();
  }

  bindEvents() {
    // Listen for discount calculations
    document.addEventListener('nms:discount-calculated', this.handleDiscountCalculated);

    // Bind scroll button if it exists
    if (this.scrollButton) {
      this.scrollButton.addEventListener('click', this.handleScrollButtonClick);
      this.scrollButton.addEventListener('keydown', this.handleScrollButtonKeydown);
    }
  }

  handleDiscountCalculated(event) {
    if (!this.lowestPriceAmount || !event.detail) return;

    const discountData = event.detail;
    if (discountData.hasDiscount && discountData.lowestPriceFormatted) {
      this.updatePrice(discountData.lowestPriceFormatted);
    }
  }

  handleScrollButtonClick(event) {
    event.preventDefault();
    this.addButtonFeedback();
    this.scrollToDiscounts();
  }

  handleScrollButtonKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.addButtonFeedback();
      this.scrollToDiscounts();
    }
  }

  updatePrice(formattedPrice) {
    if (!this.lowestPriceAmount) return;

    // Use pre-calculated price or get from global data
    const price =
      formattedPrice || (window.nmsLowestDiscountPrice && window.nmsLowestDiscountPrice.lowestPriceFormatted);

    if (price) {
      this.lowestPriceAmount.textContent = price;
    }
  }

  addButtonFeedback() {
    if (!this.scrollButton) return;

    this.scrollButton.classList.add('clicked');
    setTimeout(() => {
      this.scrollButton.classList.remove('clicked');
    }, 300);
  }

  scrollToDiscounts() {
    const discountBlocks = document.querySelectorAll(
      '[data-nms-js="buybox-accordion"][data-accordion-type="discounts"]'
    );

    if (!discountBlocks || discountBlocks.length === 0) return;

    // Find the visible discount block (mobile or desktop)
    let targetElement = null;
    for (const block of discountBlocks) {
      const rect = block.getBoundingClientRect();
      const isVisible =
        rect.width > 0 &&
        rect.height > 0 &&
        window.getComputedStyle(block).display !== 'none' &&
        window.getComputedStyle(block).visibility !== 'hidden';

      if (isVisible) {
        targetElement = block;
        break;
      }
    }

    if (!targetElement) return;

    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });

    this.dispatchCustomEvent('scroll-to-offers', {
      targetElement: targetElement,
    });
  }

  // Public API methods
  refresh() {
    this.updatePrice();
  }

  destroy() {
    // Remove event listeners
    document.removeEventListener('nms:discount-calculated', this.handleDiscountCalculated);

    if (this.scrollButton) {
      this.scrollButton.removeEventListener('click', this.handleScrollButtonClick);
      this.scrollButton.removeEventListener('keydown', this.handleScrollButtonKeydown);
    }

    super.destroy();
  }
}

// Register component
window.neemans.NMSPDPLowestPrice = NMSPDPLowestPrice;

/**
 * Auto-initialization for lowest price components
 */
const initLowestPriceComponents = () => {
  const components = document.querySelectorAll('[data-nms-js="pdp-lowest-price"]:not([data-lowest-price-initialized])');
  components.forEach(component => {
    new NMSPDPLowestPrice(component);
    component.dataset.lowestPriceInitialized = 'true';
  });
};

// Initialize on DOM ready or immediately if already loaded
document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initLowestPriceComponents)
  : initLowestPriceComponents();

// Re-initialize on dynamic content changes
document.addEventListener('shopify:section:load', initLowestPriceComponents);

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;

  const components = container.querySelectorAll(
    '[data-nms-js="pdp-lowest-price"]:not([data-lowest-price-initialized])'
  );
  components.forEach(component => {
    new NMSPDPLowestPrice(component);
    component.dataset.lowestPriceInitialized = 'true';
  });
});
